import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {useMeasureElement} from '../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {TagModel} from '../../../models/api/tagModel';
import {OverflowWithCount} from '../../common/overflow/overflowWithCount';
import {TagPill} from '../../common/tags/tagPill';

/*
 * Constants.
 */

const maxNumberInHiddenTagsIndicator = 9;

/*
 * Types.
 */

interface ConversationCardTagsProps {
  tags: Array<TagModel>;
}

/*
 * Style.
 */

interface StyledTagPillProps {
  $isLastChild: boolean;
}
const StyledTagPill = styled(TagPill)<StyledTagPillProps>`
  ${(p) =>
    !p.$isLastChild &&
    css`
      margin-right: 5px;
    `}
`;

const StyledHiddenTagsIndicatorDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade70};
  border-radius: 2px;
  color: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  padding: 2px 3px;
`;

/*
 * Component.
 */

export const ConversationCardTags: FC<ConversationCardTagsProps> = (props) => {
  const {tags} = props;
  const [containerRef, {width}] = useMeasureElement();

  const hasTags = tags.length > 0;
  if (!hasTags) {
    return null;
  }

  const renderElement = (tag: TagModel, index: number) => (
    <StyledTagPill key={tag.id} tag={tag} $isLastChild={index === tags.length - 1} />
  );

  return (
    <div ref={containerRef}>
      <OverflowWithCount<TagModel>
        elements={tags}
        overflowContainerWidth={width}
        renderElement={renderElement}
        renderOverflowIndicator={renderOverflowIndicator}
      />
    </div>
  );
};

/*
 * Helpers.
 */

const renderOverflowIndicator = (numberOfHiddenTags: number) => {
  const displayNumber =
    numberOfHiddenTags > maxNumberInHiddenTagsIndicator ? maxNumberInHiddenTagsIndicator : numberOfHiddenTags;
  return <StyledHiddenTagsIndicatorDiv>+{displayNumber}</StyledHiddenTagsIndicatorDiv>;
};
